import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, updateDoc, doc, Timestamp, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import * as XLSX from "xlsx";
import { calculateNextServiceDate, getServiceInterval } from "../utils/serviceHelpers";
import { toast } from "react-toastify";
import Loader from "../components/Loader";

function ServiceScheduler() {
    const [services, setServices] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [filterType, setFilterType] = useState("ALL"); // ALL, UPCOMING, OVERDUE
    const [searchTerm, setSearchTerm] = useState("");
    const [workTypeFilter, setWorkTypeFilter] = useState("ALL"); // ALL, Installation, Regular Service, AMC, Breakdown

    // Modals
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [allAssignments, setAllAssignments] = useState([]);

    // Date Filters
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Form State
    const [actionForm, setActionForm] = useState({
        date: "",
        remarks: "",
    });

    // Helper to Create Local Date at Midnight from "YYYY-MM-DD"
    const createLocalDate = (dateStr) => {
        if (!dateStr) return null;
        const [y, m, d] = dateStr.split("-").map(Number);
        return new Date(y, m - 1, d);
    };

    const getStatus = (nextDate) => {
        if (!nextDate) return "UNKNOWN";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return nextDate < today ? "OVERDUE" : "UPCOMING";
    };

    const toDateObj = (v) => {
        if (v instanceof Timestamp) return new Date(v.seconds * 1000);
        const d = new Date(v);
        return d.toString() === "Invalid Date" ? null : d;
    };

    const formatDate = (dateObj) => {
        if (!dateObj) return "-";
        return `${String(dateObj.getDate()).padStart(2, "0")}/${String(
            dateObj.getMonth() + 1
        ).padStart(2, "0")}/${dateObj.getFullYear()}`;
    };

    const hasRequiredSkill = (technoName, worktype) => {
        if (!technoName || !worktype) return { status: 'UNKNOWN', message: 'No technician assigned' };

        // Find employee by name or mobile (matching techno field in CASHBOOK)
        const emp = employees.find(e => e.name === technoName || e.mobile === technoName);
        if (!emp) return { status: 'NOT_FOUND', message: 'Technician not found in system' };

        // Define skill requirements
        const requirements = {
            'Installation': 'AC Installation Mastery',
            'Regular Service': 'Basic AC Service',
            'AMC': 'AMC Protocol',
            'Breakdown': 'Advanced Troubleshooting',
            'Gas Filling': 'Gas Charging Safety'
        };

        const requiredTraining = requirements[worktype];
        if (!requiredTraining) return { status: 'VERIFIED', message: 'Generic skill sufficient' };

        // Check if employee has completed this training
        const hasSkill = allAssignments.some(a =>
            a.employeeId === emp.id &&
            a.trainingName === requiredTraining &&
            a.status === 'Completed'
        );

        return hasSkill
            ? { status: 'VERIFIED', message: `Verified: ${requiredTraining}` }
            : { status: 'UNVERIFIED', message: `Needs: ${requiredTraining}` };
    };

    const fetchServices = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "CASHBOOK"), orderBy("createddate", "desc"));
            const snap = await getDocs(q);

            const rows = snap.docs
                .map((d) => {
                    const data = d.data();
                    const nextDate = toDateObj(data.nextServiceDate);

                    if (!nextDate) return null;

                    return {
                        id: d.id,
                        ...data,
                        nextDateObj: nextDate,
                        status: getStatus(nextDate),
                    };
                })
                .filter(Boolean);

            rows.sort((a, b) => a.nextDateObj - b.nextDateObj);

            setServices(rows);
        } catch (err) {
            console.error("Error fetching services:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
        fetchEmployeesAndSkills();
    }, []);

    const fetchEmployeesAndSkills = async () => {
        try {
            const eSnap = await getDocs(collection(db, "EMPLOYEES"));
            setEmployees(eSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            const aSnap = await getDocs(collection(db, "TRAINING_ASSIGNMENTS"));
            setAllAssignments(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching skill data:", err);
        }
    };

    useEffect(() => {
        let res = [];

        if (filterType === "COMPLETED") {
            // Flatten logs to find completed entries
            // Flatten logs to find completed/history entries
            services.forEach(service => {
                const logs = service.remarksLog || [];
                logs.forEach((log, idx) => {
                    if (["COMPLETED", "CANCELLED", "COMMENT", "RESCHEDULED"].includes(log.action)) {
                        res.push({
                            ...service, // Parent service details
                            id: `${service.id}_log_${idx}`, // Unique ID for table key
                            parentId: service.id,
                            completedDate: log.completedDate || log.date.toDate().toISOString().split('T')[0], // Use log date for non-completed actions
                            remarks: log.remarks, // Remarks from log
                            actionType: log.action, // Store action type for display
                            logDate: log.date // Timestamp of action
                        });
                    }
                });
            });

            // Sort by completed date (descending)
            res.sort((a, b) => {
                const dateA = new Date(a.completedDate || 0);
                const dateB = new Date(b.completedDate || 0);
                return dateB - dateA;
            });

        } else {
            // Normal filtering for Upcoming/Overdue/All
            res = services;
            if (filterType !== "ALL") {
                res = res.filter((r) => r.status === filterType);
            }
        }

        if (workTypeFilter !== "ALL") {
            res = res.filter((r) => r.worktype === workTypeFilter);
        }

        // Date Range Filtering
        if (startDate || endDate) {
            const start = startDate ? new Date(startDate) : new Date("1900-01-01");
            const end = endDate ? new Date(endDate) : new Date("2100-01-01");
            end.setHours(23, 59, 59, 999); // Include the end date fully

            res = res.filter((r) => {
                let d;
                if (filterType === "COMPLETED") {
                    // Check completedDate (YYYY-MM-DD string)
                    d = r.completedDate ? new Date(r.completedDate) : null;
                } else {
                    // Check nextDateObj
                    d = r.nextDateObj;
                }
                if (!d) return false;
                return d >= start && d <= end;
            });
        }

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            res = res.filter(
                (r) =>
                    (r.customerdetail && r.customerdetail.toLowerCase().includes(lower)) ||
                    (r.mobile && r.mobile.includes(lower)) ||
                    (r.ticket && r.ticket.toLowerCase().includes(lower))
            );
        }

        setFiltered(res);
        setCurrentPage(1); // Reset to first page on filter change
    }, [filterType, workTypeFilter, searchTerm, services, startDate, endDate]);

    const handleTechnicianChange = async (service, newTechName) => {
        try {
            await updateDoc(doc(db, "CASHBOOK", service.id), {
                techno: newTechName,
                remarksLog: arrayUnion({
                    action: "TECH_ASSIGNED",
                    remarks: `Assigned technician: ${newTechName}`,
                    date: Timestamp.now(),
                }),
            });
            toast.success(`Technician assigned: ${newTechName}`);
            fetchServices();
        } catch (err) {
            console.error("Failed to assign technician:", err);
            toast.error("Failed to assign technician");
        }
    };

    const handleIntervalChange = async (service, newInterval) => {
        const confirm = window.confirm(`Change interval to ${newInterval} months? This will recalculate the Next Service Date based on the LAST Completed Date.`);
        if (!confirm) return;

        try {
            // Find completion date in logs for non-history records, or use row date
            const completedDate = toDateObj(service.date);
            if (!completedDate) {
                alert("Cannot Calculate: Completed Date is missing.");
                return;
            }

            const nextDate = new Date(completedDate);
            nextDate.setMonth(nextDate.getMonth() + Number(newInterval));

            const payload = {
                serviceInterval: Number(newInterval),
                nextServiceDate: Timestamp.fromDate(nextDate),
                serviceStatus: getStatus(nextDate),
                remarksLog: arrayUnion({
                    action: "INTERVAL_CHANGE",
                    remarks: `Changed interval from ${service.serviceInterval} to ${newInterval} months`,
                    date: Timestamp.now(),
                }),
            };

            await updateDoc(doc(db, "CASHBOOK", service.id), payload);
            fetchServices();
        } catch (err) {
            console.error("Failed to change interval:", err);
            alert("Failed to update interval.");
        }
    };

    const submitComplete = async () => {
        if (!actionForm.date || !actionForm.remarks) {
            alert("Date and Remarks are required.");
            return;
        }

        try {
            const completedDate = createLocalDate(actionForm.date);
            const interval = selectedService.serviceInterval || 4;

            // CHANGE: Calculate next date based on the CURRENT DUE DATE (nextServiceDate), not the completed date.
            // This ensures the schedule is maintained (e.g. 3 months from the original due date).
            // Fallback to completedDate only if nextDateObj is missing for some reason.
            const baseDate = selectedService.nextDateObj || completedDate;

            const nextDate = new Date(baseDate);
            nextDate.setMonth(nextDate.getMonth() + interval);

            const payload = {
                // 'date' field in Cashbook IS the completed/transaction date.
                date: Timestamp.fromDate(completedDate),
                nextServiceDate: Timestamp.fromDate(nextDate),
                serviceStatus: "UPCOMING", // Reset status to upcoming
                remarksLog: arrayUnion({
                    action: "COMPLETED",
                    remarks: actionForm.remarks,
                    date: Timestamp.now(),
                    completedDate: actionForm.date,
                    baseDueDateUsed: selectedService.nextDateObj ? true : false // Optional: for debugging
                }),
            };

            await updateDoc(doc(db, "CASHBOOK", selectedService.id), payload);
            setShowCompleteModal(false);
            fetchServices();
        } catch (err) {
            console.error("Failed to mark completed:", err);
            alert("Failed to save.");
        }
    };

    const submitReschedule = async () => {
        if (!actionForm.date || !actionForm.remarks) {
            alert("Date and Remarks are required.");
            return;
        }

        try {
            const newNextDate = createLocalDate(actionForm.date);

            const payload = {
                nextServiceDate: Timestamp.fromDate(newNextDate),
                serviceStatus: getStatus(newNextDate),
                remarksLog: arrayUnion({
                    action: "RESCHEDULED",
                    remarks: actionForm.remarks,
                    date: Timestamp.now(),
                    rescheduledTo: actionForm.date,
                }),
            };

            await updateDoc(doc(db, "CASHBOOK", selectedService.id), payload);
            setShowRescheduleModal(false);
            fetchServices();
        } catch (err) {
            console.error("Failed to reschedule:", err);
            alert("Failed to save.");
        }
    };

    const submitCancel = async () => {
        if (!actionForm.remarks) {
            alert("Remarks are required to cancel.");
            return;
        }

        const confirm = window.confirm("Are you sure you want to CANCEL this service? The schedule will advance to the next interval.");
        if (!confirm) return;

        try {
            // Logic similar to Complete: Advance date based on baseDueDate
            const interval = selectedService.serviceInterval || 4;
            const completedDate = new Date(); // Use today as 'cancelled date' reference if needed
            const baseDate = selectedService.nextDateObj || completedDate;

            const nextDate = new Date(baseDate);
            nextDate.setMonth(nextDate.getMonth() + interval);

            const payload = {
                nextServiceDate: Timestamp.fromDate(nextDate),
                serviceStatus: "UPCOMING",
                remarksLog: arrayUnion({
                    action: "CANCELLED",
                    remarks: actionForm.remarks,
                    date: Timestamp.now(),
                    cancelledDate: new Date().toISOString().split('T')[0]
                }),
            };

            await updateDoc(doc(db, "CASHBOOK", selectedService.id), payload);
            setShowCancelModal(false);
            fetchServices();
        } catch (err) {
            console.error("Failed to cancel service:", err);
            alert("Failed to cancel.");
        }
    };

    const submitComment = async () => {
        if (!actionForm.remarks) {
            alert("Remarks are required.");
            return;
        }

        try {
            const payload = {
                remarksLog: arrayUnion({
                    action: "COMMENT",
                    remarks: actionForm.remarks,
                    date: Timestamp.now(),
                }),
            };

            await updateDoc(doc(db, "CASHBOOK", selectedService.id), payload);
            setShowCommentModal(false);
            fetchServices();
        } catch (err) {
            console.error("Failed to add comment:", err);
            alert("Failed to add comment.");
        }
    };

    const handleExport = () => {
        if (filtered.length === 0) {
            alert("No data to export");
            return;
        }

        const data = filtered.map((r, i) => ({
            "#": i + 1,
            "Status": filterType === "COMPLETED" ? "COMPLETED" : r.status,
            "Ticket": r.ticket || "-",
            "Next Service Date": filterType === "COMPLETED" ? "-" : formatDate(r.nextDateObj),
            "Completed Date": filterType === "COMPLETED" ? r.completedDate : "-",
            "Customer": r.customerdetail,
            "Mobile": r.mobile,
            "Work Type": r.worktype,
            "Remarks": filterType === "COMPLETED" ? r.remarks : (r.remarksLog && r.remarksLog.length > 0 ? r.remarksLog[r.remarksLog.length - 1].remarks : "-")
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Services");
        XLSX.writeFile(wb, `Service_Scheduler_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleSyncDates = async () => {
        const confirm = window.confirm("This will calculate and fill missing 'Next Service Date' for all eligible records. Continue?");
        if (!confirm) return;

        setLoading(true);
        try {
            const snap = await getDocs(collection(db, "CASHBOOK"));
            let updatedCount = 0;
            const updates = [];

            for (const d of snap.docs) {
                const data = d.data();

                // 1. FILL MISSING DATES for valid types
                if (!data.nextServiceDate && ["Installation", "Regular Service", "AMC"].includes(data.worktype)) {
                    const nextDate = calculateNextServiceDate(data.date, data.worktype);
                    if (nextDate) {
                        const interval = getServiceInterval(data.worktype);
                        const status = getStatus(nextDate);
                        updates.push(
                            updateDoc(doc(db, "CASHBOOK", d.id), {
                                nextServiceDate: Timestamp.fromDate(nextDate),
                                serviceInterval: interval,
                                serviceStatus: status
                            })
                        );
                        updatedCount++;
                    }
                }

                // 2. CLEANUP INVALID DATES (e.g. Breakdown should typically not have a schedule, or 1970 dates)
                // We'll remove nextServiceDate if it's "Breakdown" OR if the date is the Epoch (1970)
                if (data.nextServiceDate) {
                    const dateObj = data.nextServiceDate.toDate ? data.nextServiceDate.toDate() : new Date(data.nextServiceDate);
                    const isEpoch = dateObj.getFullYear() === 1970;

                    if (data.worktype === "Breakdown" || isEpoch) {
                        updates.push(
                            updateDoc(doc(db, "CASHBOOK", d.id), {
                                nextServiceDate: null,
                                serviceStatus: null
                            })
                        );
                        updatedCount++;
                    }
                }
            }

            if (updates.length > 0) {
                await Promise.all(updates);
                alert(`Successfully synced ${updatedCount} records.`);
                fetchServices(); // Refresh list
            } else {
                alert("No records needed syncing.");
                setLoading(false);
            }
        } catch (err) {
            console.error("Sync failed:", err);
            alert("Sync failed. Check console.");
            setLoading(false);
        }
    };

    const openModal = (type, service) => {
        setSelectedService(service);
        // Default to today using local YYYY-MM-DD
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        setActionForm({ date: `${yyyy}-${mm}-${dd}`, remarks: "" });

        if (type === "COMPLETE") setShowCompleteModal(true);
        if (type === "RESCHEDULE") setShowRescheduleModal(true);
        if (type === "CANCEL") setShowCancelModal(true);
        if (type === "COMMENT") setShowCommentModal(true);
    };

    if (loading) {
        return (<Loader />);
    }

    return (
        <div className="container-fluid mt-4 px-4 position-relative" style={{ minHeight: '400px' }}>
           
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Service Scheduler</h3>
                <div className="btn-group">
                    <button className={`btn ${filterType === "ALL" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setFilterType("ALL")}>All ({services.length})</button>
                    <button className={`btn ${filterType === "UPCOMING" ? "btn-success" : "btn-outline-success"}`} onClick={() => setFilterType("UPCOMING")}>Upcoming ({services.filter((s) => s.status === "UPCOMING").length})</button>
                    <button className={`btn ${filterType === "OVERDUE" ? "btn-danger" : "btn-outline-danger"}`} onClick={() => setFilterType("OVERDUE")}>Overdue ({services.filter((s) => s.status === "OVERDUE").length})</button>
                    <button className={`btn ${filterType === "COMPLETED" ? "btn-dark" : "btn-outline-dark"}`} onClick={() => setFilterType("COMPLETED")}>History ({services.reduce((acc, s) => acc + (s.remarksLog?.filter(l => ["COMPLETED", "CANCELLED", "COMMENT", "RESCHEDULED"].includes(l.action)).length || 0), 0)})</button>
                </div>
            </div>

            <div className="row g-2 mb-3 align-items-end">
                <div className="col-md-3">
                    <input type="text" className="form-control" placeholder="Search by Customer, Mobile, or Ticket..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <div className="col-md-2">
                    <label className="form-label small mb-0 text-muted">Filter Work Type</label>
                    <select className="form-select" value={workTypeFilter} onChange={(e) => setWorkTypeFilter(e.target.value)}>
                        <option value="ALL">All Work Types</option>
                        <option value="Installation">Installation</option>
                        <option value="Regular Service">Regular Service</option>
                        <option value="AMC">AMC</option>
                        <option value="Breakdown">Breakdown</option>
                    </select>
                </div>
                <div className="col-md-2">
                    <label className="form-label small mb-0 text-muted">Start Date</label>
                    <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div className="col-md-2">
                    <label className="form-label small mb-0 text-muted">End Date</label>
                    <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
                <div className="col-md-3 d-grid gap-2 d-md-flex justify-content-md-end">
                    <button className="btn btn-warning text-dark" onClick={handleSyncDates} title="Fix missing dates">
                        <i className="bi bi-arrow-repeat me-1"></i> Sync Dates
                    </button>
                    <button className="btn btn-outline-success" onClick={handleExport}>
                        <i className="bi bi-file-earmark-excel me-1"></i> Export Excel
                    </button>
                </div>
            </div>

            {/* Pagination Logic */}
            {(() => {
                const indexOfLastItem = currentPage * itemsPerPage;
                const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);
                const totalPages = Math.ceil(filtered.length / itemsPerPage);

                const paginate = (pageNumber) => setCurrentPage(pageNumber);

                return (
                    <>
                        <div className="table-responsive" style={{ overflow: 'visible' }}>
                            <table className="table table-hover table-bordered shadow-sm align-middle">
                                <thead className="table-dark">
                                    {filterType === "COMPLETED" ? (
                                        <tr>
                                            <th>#</th>
                                            <th>Completed Date</th>
                                            <th>Ticket</th>
                                            <th>Customer Name</th>
                                            <th>Work Type</th>
                                            <th>Technician</th>
                                            <th>Action Taken</th>
                                            <th>Remarks</th>
                                        </tr>
                                    ) : (
                                        <tr>
                                            <th>#</th>
                                            <th>Status</th>
                                            <th>Ticket</th>
                                            <th>Next Service Date</th>
                                            <th>Customer Name</th>
                                            <th>Technician</th>
                                            <th>Work Type</th>
                                            <th>Interval</th>
                                            <th>Actions</th>
                                        </tr>
                                    )}
                                </thead>
                                <tbody>
                                    {currentItems.map((row, index) => (
                                        <tr key={row.id}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            {filterType === "COMPLETED" ? (
                                                <>
                                                    <td className="fw-bold">{row.completedDate || "-"}</td>
                                                    <td>{row.ticket || "-"}</td>
                                                    <td>
                                                        {row.customerdetail}<br />
                                                        <small className="text-muted">{row.mobile}</small>
                                                    </td>
                                                    <td>{row.worktype}</td>
                                                    <td>
                                                        <div className="dropdown">
                                                            <button className="btn btn-sm btn-link text-decoration-none p-0 text-dark dropdown-toggle w-100 text-start d-flex align-items-center" type="button" data-bs-toggle="dropdown" title={hasRequiredSkill(row.techno, row.worktype).message}>
                                                                <span className="me-2 fw-bold small text-truncate" style={{ maxWidth: '100px' }}>{row.techno || "Assign"}</span>
                                                                {(() => {
                                                                    const skill = hasRequiredSkill(row.techno, row.worktype);
                                                                    return (
                                                                        <span className={`badge ${skill.status === 'VERIFIED' ? 'bg-success' : (skill.status === 'NOT_FOUND' ? 'bg-secondary' : 'bg-warning text-dark')} x-small`} style={{ fontSize: '0.64rem' }}>
                                                                            {skill.status === 'VERIFIED' ? 'VER' : (skill.status === 'NOT_FOUND' ? 'UNK' : 'GAP')}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </button>
                                                            <ul className="dropdown-menu shadow border-0" style={{ maxHeight: '250px', overflowY: 'auto', minWidth: '220px', zIndex: 1050 }}>
                                                                <li><h6 className="dropdown-header">Assign Technician</h6></li>
                                                                {employees.map(emp => {
                                                                    const skill = hasRequiredSkill(emp.name, row.worktype);
                                                                    return (
                                                                        <li key={emp.id}>
                                                                            <button className="dropdown-item d-flex justify-content-between align-items-center py-2" onClick={() => handleTechnicianChange(row, emp.name)}>
                                                                                <div>
                                                                                    <div className="fw-bold small">{emp.name}</div>
                                                                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>{skill.message}</div>
                                                                                </div>
                                                                                {skill.status === 'VERIFIED' && <i className="bi bi-patch-check-fill text-success"></i>}
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {row.actionType === "COMPLETED" && <span className="badge bg-success">COMPLETED</span>}
                                                        {row.actionType === "CANCELLED" && <span className="badge bg-danger">CANCELLED</span>}
                                                        {row.actionType === "RESCHEDULED" && <span className="badge bg-warning text-dark">RESCHEDULED</span>}
                                                        {row.actionType === "COMMENT" && <span className="badge bg-info text-white">COMMENT</span>}
                                                    </td>
                                                    <td>{row.remarks}</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td><span className={`badge ${row.status === "OVERDUE" ? "bg-danger" : "bg-success"}`}>{row.status}</span></td>
                                                    <td>{row.ticket || "-"}</td>
                                                    <td className="fw-bold">{formatDate(row.nextDateObj)}</td>
                                                    <td>{row.customerdetail}</td>
                                                    <td>
                                                        <div className="dropdown">
                                                            <button className="btn btn-sm btn-link text-decoration-none p-0 text-dark dropdown-toggle w-100 text-start d-flex align-items-center" type="button" data-bs-toggle="dropdown" title={hasRequiredSkill(row.techno, row.worktype).message}>
                                                                <span className="me-2 fw-bold small text-truncate" style={{ maxWidth: '100px' }}>{row.techno || "Unassigned"}</span>
                                                                {(() => {
                                                                    const skill = hasRequiredSkill(row.techno, row.worktype);
                                                                    return (
                                                                        <span className={`badge ${skill.status === 'VERIFIED' ? 'bg-success' : (skill.status === 'NOT_FOUND' ? 'bg-secondary' : 'bg-warning text-dark')} x-small`} style={{ fontSize: '0.64rem' }}>
                                                                            {skill.status === 'VERIFIED' ? 'VER' : (skill.status === 'NOT_FOUND' ? 'UNK' : 'GAP')}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </button>
                                                            <ul className="dropdown-menu shadow border-0" style={{ maxHeight: '250px', overflowY: 'auto', minWidth: '220px', zIndex: 1050 }}>
                                                                <li><h6 className="dropdown-header">Assign Qualified Tech</h6></li>
                                                                {employees.map(emp => {
                                                                    const skill = hasRequiredSkill(emp.name, row.worktype);
                                                                    return (
                                                                        <li key={emp.id}>
                                                                            <button className="dropdown-item d-flex justify-content-between align-items-center py-2" onClick={() => handleTechnicianChange(row, emp.name)}>
                                                                                <div>
                                                                                    <div className="fw-bold small">{emp.name}</div>
                                                                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>{skill.message}</div>
                                                                                </div>
                                                                                {skill.status === 'VERIFIED' && <i className="bi bi-patch-check-fill text-success"></i>}
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    </td>
                                                    <td>{row.worktype}</td>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={row.serviceInterval || 0}
                                                            onChange={(e) => handleIntervalChange(row, e.target.value)}
                                                            style={{ width: "auto" }}
                                                        >
                                                            <option value="3">3 M</option>
                                                            <option value="4">4 M</option>
                                                            <option value="6">6 M</option>
                                                            <option value="9">9 M</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-1 flex-wrap">
                                                            <button className="btn btn-sm btn-success" onClick={() => openModal("COMPLETE", row)} title="Mark Completed">Done</button>
                                                            <button className="btn btn-sm btn-warning" onClick={() => openModal("RESCHEDULE", row)} title="Reschedule">Reschedule</button>
                                                            <button className="btn btn-sm btn-danger" onClick={() => openModal("CANCEL", row)} title="Cancel Service">Cancel</button>
                                                            <button
                                                                className="btn btn-sm btn-info text-white"
                                                                onClick={() => openModal("COMMENT", row)}
                                                                title={(() => {
                                                                    const comments = row.remarksLog?.filter(l => l.action === "COMMENT") || [];
                                                                    if (comments.length === 0) return "Add Comment";
                                                                    return comments.map(c => {
                                                                        const d = c.date && c.date.seconds ? new Date(c.date.seconds * 1000) : new Date();
                                                                        return `${d.toLocaleDateString()}: ${c.remarks}`;
                                                                    }).join("\n");
                                                                })()}
                                                            >
                                                                ...
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filtered.length === 0 && <div className="text-center p-3">No records found.</div>}
                        </div>

                        {/* Pagination Controls */}
                        {filtered.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="d-flex align-items-center gap-2">
                                    <span>Show</span>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: "auto" }}
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                    <span>entries</span>
                                    <span className="ms-2 text-muted small">
                                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filtered.length)} of {filtered.length}
                                    </span>
                                </div>

                                <nav aria-label="Page navigation">
                                    <ul className="pagination pagination-sm mb-0">
                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
                                        </li>
                                        {[...Array(totalPages)].map((_, i) => {
                                            const page = i + 1;
                                            if (
                                                page === 1 ||
                                                page === totalPages ||
                                                (page >= currentPage - 2 && page <= currentPage + 2)
                                            ) {
                                                return (
                                                    <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                                                        <button className="page-link" onClick={() => paginate(page)}>{page}</button>
                                                    </li>
                                                );
                                            } else if (
                                                (page === currentPage - 3 && currentPage > 4) ||
                                                (page === currentPage + 3 && currentPage < totalPages - 3)
                                            ) {
                                                return <li key={page} className="page-item disabled"><span className="page-link">...</span></li>;
                                            }
                                            return null;
                                        })}
                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </>
                );
            })()}

            {/* COMPLETE MODAL */}
            {showCompleteModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Mark Service as Completed</h5>
                                <button className="btn-close" onClick={() => setShowCompleteModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Completed Date</label>
                                    <input type="date" className="form-control" value={actionForm.date} onChange={(e) => setActionForm({ ...actionForm, date: e.target.value })} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Remarks (Required)</label>
                                    <textarea className="form-control" rows="3" value={actionForm.remarks} onChange={(e) => setActionForm({ ...actionForm, remarks: e.target.value })}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowCompleteModal(false)}>Cancel</button>
                                <button className="btn btn-success" onClick={submitComplete}>Save & Updates</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* RESCHEDULE MODAL */}
            {showRescheduleModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Reschedule Service</h5>
                                <button className="btn-close" onClick={() => setShowRescheduleModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">New Service Date</label>
                                    <input type="date" className="form-control" value={actionForm.date} onChange={(e) => setActionForm({ ...actionForm, date: e.target.value })} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Remarks (Required)</label>
                                    <textarea className="form-control" rows="3" value={actionForm.remarks} onChange={(e) => setActionForm({ ...actionForm, remarks: e.target.value })}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowRescheduleModal(false)}>Cancel</button>
                                <button className="btn btn-warning" onClick={submitReschedule}>Save Reschedule</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CANCEL MODAL */}
            {showCancelModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-danger">Cancel Service</h5>
                                <button className="btn-close" onClick={() => setShowCancelModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning">
                                    This will <strong>skip</strong> the current service and schedule the next occurrence based on the interval.
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Remarks (Required)</label>
                                    <textarea className="form-control" rows="3" value={actionForm.remarks} onChange={(e) => setActionForm({ ...actionForm, remarks: e.target.value })} placeholder="Reason for cancellation..."></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>Back</button>
                                <button className="btn btn-danger" onClick={submitCancel}>Confirm Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* COMMENT MODAL */}
            {showCommentModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Comment</h5>
                                <button className="btn-close" onClick={() => setShowCommentModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Comment</label>
                                    <textarea className="form-control" rows="3" value={actionForm.remarks} onChange={(e) => setActionForm({ ...actionForm, remarks: e.target.value })} placeholder="Enter note..."></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowCommentModal(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitComment}>Save Comment</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ServiceScheduler;
